import * as React from 'react';
import * as _ from "lodash";
import ReactDataGrid, {GridRowsUpdatedEvent, SelectionParams, OnRowExpandToggle} from 'react-data-grid';
import {DraggableHeader, Data} from 'react-data-grid-addons';
import {BaseColumnClass, BaseColumn, IValueState, IColumnDefinition, EditableValue as ColEditableValue, Filter} from '../components/BaseColumn';

// i18n
import {translate} from 'react-i18next';

import {DerivateOptionColumnDefinition, IDerivateOptionColumnDefinition} from './DerivateOptionColumn';
import {TDerivateStates, EOptionState} from './DerivateOptionBar';
import {ITranslatedComponent} from '../redux/reducers/generalReducer';
import {Icon} from 'semantic-ui-react';
import deepEqual from 'deep-equal';

export import EditableValue = ColEditableValue;
import ColumnFactory from '../services/ColumnFactory';

interface IRowGroupRendererProps extends ITranslatedComponent {
    columnGroupName: string;
    name: string;
    isExpanded: boolean;
    onRowExpandClick(): void;
    onRowExpandToggle(): void;
}

/** renders the group row in table */
class RowGroupRenderer extends React.Component<IRowGroupRendererProps> {
    render() {
        const {isExpanded = true, columnGroupName = '', name = '', onRowExpandClick, t} = this.props;

        return (
            <div onClick={onRowExpandClick}>
                <Icon name={isExpanded ? "folder open" : "folder"} />
                <strong>{t(columnGroupName.toUpperCase()) + ': ' + name}</strong>
            </div>
        );
    }
}
const RowGroupRendererTranslated = translate("COLUMN_FACTORY")(RowGroupRenderer);

/** Settings for value state */
interface ValueState extends IValueState {
    // disable some properties
    onChange?: undefined;
}

/**
 * Information about a specific column to be rendered.
 */
export interface Column extends IColumnDefinition {
    /**
     * If defined, the StateViewBar will be shown. See the property 'valueStateHeight' of the BasicDataGrid to set it visible.
     */
    valueState?: ValueState;

    /**
     * The minimum height of the row in pixels for this column.
     * @default 35
     */
    minRowHeight?: number;

    /**
     * The editor and formatter for this column. Should be inherited from ABaseColumn.
     * For Base Implementation you can use BaseColumn.
     *
     * @default BaseColumn
     */
    cellRenderer?: BaseColumnClass;

    // disable some properties
    editable?: undefined; // see property 'keyEditable'
    valueStateHeight?: undefined; // defined in IBasicDataGridProps
    cellClass?: undefined;
    editor?: undefined;
    formatter?: undefined;
    events?: undefined;
    filterRenderer?: undefined;
    filterValid?: undefined;
    originIndex?: undefined;
    rowKey?: undefined;
}

/** row selection type */
export enum ERowSelect {
    Disabled = 'disabled',
    Single = 'single',
    Multi = 'multi'
}

interface IBasicDataGridProps {

    /** The columns to render. */
    columns: Array<Column>;
    /** The data to render in each row. */
    elements: Array<object>;

    /**
     * Defines, if the cells can switch in edit mode.
     * Futher, each Column has property 'keyEditable' defining whether the column can be edited.
     * And, this property 'keyEditable' points to the property of the row, which defines if the
     * cell in the column can be edited.
     *
     * @default false
     */
    editable?: boolean;

    /**
     * When set to Multi, enables multiple row select.
     * When set to Single, enables single row select.
     * When set to Disabled or not set, disables row select.
     *
     * See onRowSelectionChanged, selectedElements
     *
     * @default Disabled
     */
    rowSelect?: ERowSelect;

    /**
     * Whether or not to show the 'select all' checkbox in the select column.
     *
     * @default true
     */
    showSelectAll?: boolean;

    /**
     * if rowSelect is not disabled, defines the selected elements.
     */
    selectedElements?: Array<object>;

    /**
     * A property that's unique to every row.
     * This property is required to enable row selection.
     * @default 'id'
     */
    rowKey?: string;

    /**
     * The minimum width of the entire grid in pixels.
     */
    minWidth?: number;
    /**
     * The minimum height of the entire grid in pixels.
     * @default 350
     */
    minHeight?: number;
    /**
     * The height of the value state view bar.
     * If 0, the valueState will not be shown and the space is not allocated.
     * @default 0
     */
    valueStateHeight?: number;
    /**
     * The height of the header row in pixels.
     * @default 35
     */
    headerRowHeight?: number;

    /**
     * The height of the header filter row in pixels.
     * @default 45
     */
    headerFiltersHeight?: number;

    /**
     * shows filters in header if true
     * @default false
     */
    showFilter?: boolean;

    /**
     * defines if the reordering of columns is enabled
     * @default false
     */
    enableReorderColumns?: boolean;

    /**
     * The minimum width of each column in pixels.
     * @default 20
     */
    minColumnWidth?: number;

    /**
     * this event will be called on changing of the values.
     * use update for rerendering of changed cells, see example!
     *
     * @param cellKey the property which is changed
     * @param values the objects with the changed property (only the properties rowKey and cellKey will be set)
     *
     * @example
     *    import update from 'react-addons-update';
     *    * * *
     *    onChange(cellKey: string, values: Array<IElement>): void
     *     {
     *       if (this.state.rows !== null)
     *       {
     *         let rows = this.state.rows.slice();
     *         values.forEach( (value: IElement) =>
     *           {
     *             const index = rows.findIndex((val: IElement) => val.id === value.id);
     *             if (index >= 0)
     *             {
     *               let rowToUpdate = rows[index];
     *               let updatedRow = update(rowToUpdate, {$merge: value}); // use update for rerendering of changed cells!!!
     *               rows[index] = updatedRow;
     *             }
     *           }
     *         );
     *         this.setState({rows});
     *       }
     *     }
     */
    onChange?(cellKey: string, values: Array<object>): void;

    /**
     * if defined, will be called for each item be selected.
     * @param value value to be checked if it is selectable
     * @returns whether the value is selectable
     */
    onCheckRowSelectable?(value: object): boolean;

    /**
     * this event will be called, when the selection of rows is changed.
     *
     * @param values list of currently selected rows
     */
    onRowSelectionChanged?(values: Array<object>): void;

    /**
     * this event will be called, when the user has moved the columns.
     * See enableReorderColumns.
     * @param columns columns keys in the new order
     */
    onReorderColumns?(columns: string[]): void;

    onFilterChange?: (filter: Filter) => void;
}

interface IBasicDataGridState {
    /** column definitions */
    columns: Array<IColumnDefinition>;
    /** Stores the sort direction through property changes. */
    sortDirection: string;
    /** Stores the sort column through property changes. */
    sortColumnKey: string | undefined;
    /** Shallow copy of props.elements in the intended order. */
    sortedElements: Array<any>;

    /** selected rows by 'rowKey' */
    selectedRows: Array<any>;

    filters:
    {
        [key: string]: Filter;
    };

    /** grouping by this column keys */
    groupBy: Array<string>;

    /** rows to be expanded */
    expandedRows: any;

    /** is true when reordering */
    isReordering: boolean;

    /**
     * The height of each individual row in pixels. Calculated based on minRowHeight of Columns in Property.
     */
    rowHeight: number;

    /** current state of show filter */
    showFilter: boolean;
}

// FIXME: bug in the ReactDataGrid - Uncaught TypeError: Cannot read property 'canvas' of undefined
// class EmptyRowsView extends React.Component<ITranslatedComponent>
// {
//     render() {
//         return <div>{this.props.t('No elements to show')}</div>;
//     }
// }
// const EmptyRowsViewTrans = translate('BasicDataGrid')(EmptyRowsView);

/**
 * Wrapper of react-data-grid (http://adazzle.github.io/react-data-grid/index.html#)
 * Can be used with any object array.
 * @param props containing at least array of elements and column definition
 *
 * @author rbeyer, akirillov
 */
// TODO XSVX001-200 This component is no longer a dynamic one
// since it's used only for TechDataView and should be refactored
class BasicDataGrid extends React.Component<IBasicDataGridProps, IBasicDataGridState> {
    node: ReactDataGrid | null;
    constructor(props: IBasicDataGridProps) {
        super(props);

        const {rowKey = 'id'} = this.props;

        const {rowSelect = ERowSelect.Disabled} = props;
        let selectedRows = rowSelect !== ERowSelect.Disabled && props.selectedElements ?
            props.selectedElements.map(row => row[rowKey]) : [];
        if (rowSelect === ERowSelect.Single && selectedRows.length > 1) {
            selectedRows = selectedRows.slice(0, 1);
        }

        this.state = {
            columns: this.mapColumns(props),
            rowHeight: BasicDataGrid.calcRowHeight(props),
            sortDirection: 'NONE',
            sortColumnKey: undefined,
            sortedElements: props.elements.slice(0),
            selectedRows,
            filters: {},
            groupBy: [],
            expandedRows: {},
            isReordering: false,
            showFilter: false
        };

        this.getColumn = this.getColumn.bind(this);
        this.getRows = this.getRows.bind(this);
        this.getRow = this.getRow.bind(this);
        this.handleGridSort = this.handleGridSort.bind(this);
        this.handleGridRowsUpdated = this.handleGridRowsUpdated.bind(this);
        this.mapColumns = this.mapColumns.bind(this);
        this.handleGridCellValueStateUpdated = this.handleGridCellValueStateUpdated.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handleClearFilters = this.handleClearFilters.bind(this);
        this.handleColumnResize = this.handleColumnResize.bind(this);
        this.getSelectedRows = this.getSelectedRows.bind(this);
        this.onRowsSelected = this.onRowsSelected.bind(this);
        this.onRowsDeselected = this.onRowsDeselected.bind(this);
        this.onRowExpandToggle = this.onRowExpandToggle.bind(this);
        this.onHeaderDrop = this.onHeaderDrop.bind(this);
        this.isSameColumn = this.isSameColumn.bind(this);
        this.toggleFilter = this.toggleFilter.bind(this);
    }

    componentDidMount() {
        const {showFilter = false} = this.props;
        this.setState({showFilter: false}, () => {
            // workaround: ReactDataGrid needs to be filter deactivated at the beginning.
            // so set it after some time. Otherwise it will not render the column with index > 3.
            if (showFilter) {
                this.setState({showFilter});
            }
        });
    }

    componentWillReceiveProps(nextProps: Readonly<IBasicDataGridProps>) {
        const {sortDirection, sortColumnKey} = this.state;
        const sortedElements = nextProps.elements.slice(0);
        let nextState: any = {};
        let restoreState: any = {};
        if (sortDirection !== 'NONE' && sortColumnKey) {
            sortedElements.sort(this.getComparer(sortColumnKey, sortDirection));
        }
        nextState.sortedElements = sortedElements;

        const {showFilter = false} = nextProps;
        if (showFilter !== this.state.showFilter) {
            nextState.showFilter = showFilter;
        }

        if (this.props.enableReorderColumns !== nextProps.enableReorderColumns) {
            // FIXME: bug in ReactDataGrid: deactivate filter and activate again
            nextState.showFilter = false;
            if (showFilter) {
                restoreState.showFilter = showFilter;
            }
        }

        const columnsEqual = _.isEqual(this.props.columns, nextProps.columns);
        if (!columnsEqual) {
            nextState.filters = {}; // clear filter on changing columns
        }

        if (!columnsEqual
            || this.props.valueStateHeight !== nextProps.valueStateHeight) {
            nextState.rowHeight = BasicDataGrid.calcRowHeight(nextProps);
            nextState.columns = this.mapColumns(nextProps, {props: this.props, columns: this.state.columns});
        }
        else if (this.props.enableReorderColumns !== nextProps.enableReorderColumns) {
            nextState.columns = this.state.columns.map(column => {
                return {...column, draggable: nextProps.enableReorderColumns};
            });
        }

        const {rowSelect = ERowSelect.Disabled} = this.props;
        const rowSelectNext = nextProps.rowSelect === undefined ? ERowSelect.Disabled : nextProps.rowSelect;

        const selectedRowsChanged = rowSelectNext !== rowSelect
            || nextProps.rowKey !== this.props.rowKey;
        if (selectedRowsChanged || nextProps.selectedElements !== this.props.selectedElements) {
            const {rowKey = 'id'} = this.props;
            const selectedRows = nextProps.selectedElements === this.props.selectedElements ? this.state.selectedRows :
                nextProps.selectedElements ? nextProps.selectedElements.map(row => row[rowKey]) : [];
            nextState.selectedRows = this.getSelectedRows(nextProps, selectedRows);
        }

        // FIXME: there is a bug in ReactDataGrid the rows will be not reread. So remove it, when the bug is fixed
        const hack = rowSelect === ERowSelect.Disabled && rowSelectNext !== ERowSelect.Disabled ? 1 :
            rowSelect !== ERowSelect.Disabled && rowSelectNext === ERowSelect.Disabled ? -1 : 0;

        if (hack !== 0 && this.state.columns.length > 0) {
            const hackColumn = {...this.state.columns[0]};
            if (hackColumn.width) {
                hackColumn.width = hackColumn.width + hack;
            }
            nextState.columns = [...this.state.columns];
            nextState.columns[0] = hackColumn;
        }
        // hack end

        this.setState(nextState, () => {
            if (selectedRowsChanged) {
                this.callRowSelectionChanged();
            }
            if (Object.getOwnPropertyNames(restoreState).length > 0) {
                this.forceUpdate(() => this.setState(restoreState));
            }
        });
    }

    /**
     * gets the first column with the key
     * @param cellKey the key of the column.
     */
    getColumn(cellKey: string): IColumnDefinition | undefined {
        return _.find(this.state.columns, (value: IColumnDefinition) => value.key === cellKey);
    }

    /**
     * filter out the options, which have all option states for not hidden derivates set to n.A.
     * depends on column.showNotAvailableOptions.
     * @param row option to be checked for filter out
     * @returns true if not filter out, false if filter out
     */
    filterByNotHiddenDerivates(row: any): boolean {
        // implement outside of filterValid, for using without active filter
        const columns = this.state.columns;
        let result = true;
        columns.forEach((element) => {
            const column = element as IDerivateOptionColumnDefinition;
            if (column.showNotAvailableOptions === false) // the not IDerivateOptionColumnDefinition will have this property as undefined
            {
                const hideDerivates: Array<string> = column.hideDerivates ? column.hideDerivates : [];

                const derivateStates = row[column.key] as TDerivateStates;
                const derivates = Object.keys(derivateStates);
                let hasNormal = 0 <= _.findIndex(derivates, derivate => (_.findIndex(hideDerivates, hideValue => hideValue === derivate) < 0
                    && derivateStates[derivate] > EOptionState.NotAvailable));

                if (!hasNormal) {
                    result = false;
                }
            }
        });
        return result;
    }

    /**
     * filter out the options, which do not pass at least one of the defined filters.
     * @param row option to be checked for filter out
     * @returns true if not filter out, false if filter out
     */
    filterByFilters(row: any): boolean {
        const keys: string[] = Object.getOwnPropertyNames(this.state.filters);
        let result = true;

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const filter = this.state.filters[key];
            if (!filter.column.filterValid || !filter.column.filterValid(filter, row)) {
                result = false;
                break;
            }
        }
        return result;
    }

    getRows(): Array<any> {

        const filteredRows = this.state.sortedElements.filter((row: any) => {
            return this.filterByFilters(row) && this.filterByNotHiddenDerivates(row);
        });

        const {columns, groupBy, expandedRows} = this.state;
        const sel = {
            groupBy: columns.length > 0 ? groupBy : [],
            expandedRows: columns.length > 0 ? expandedRows : [],
            rows: filteredRows
        };
        // TODO: fix the group representation (now the column key is used)
        return Data.Selectors.getRows(sel);
    }

    /**
     * Gets single row for the table.
     * Required!
     * @param i index
     * @returns row
     */
    getRow(i: number): object {
        return this.getRows()[i];
    }

    /**
     * Gets the compare function to be used when sorting row elements.
     * @param columnName the column by which to sort
     * @param sortDirection the sort direction
     * @returns a function to be passed into Array.sort()
     */
    getComparer(columnName: string, sortDirection: string) {
        return (a: any, b: any) => {
            const aStr = a[columnName] !== undefined && a[columnName] !== null ? String(a[columnName]).toUpperCase() : "";
            const bStr = b[columnName] !== undefined && b[columnName] !== null ? String(b[columnName]).toUpperCase() : "";
            if (sortDirection === 'ASC') {
                return (aStr > bStr) ? 1 : -1;
            } else if (sortDirection === 'DESC') {
                return (aStr < bStr) ? 1 : -1;
            } else {
                return 0;
            }
        };
    }

    /**
     * Handles sorting of columns if they have attribute sortable set to true
     * @param columnKey key of the column with sortable == true
     * @param sortDirection
     * @returns sorted rows
     */
    handleGridSort(columnKey: string, sortDirection: string): void {
        const rows = (sortDirection === 'NONE')
            ? this.props.elements.slice(0)
            : this.state.sortedElements.sort(this.getComparer(columnKey, sortDirection));

        let newState = {sortDirection: sortDirection, sortColumnKey: columnKey, sortedElements: rows, groupBy: this.state.groupBy.slice(0, 0)};

        // group by column if groupable
        let activeColumn = _.find(this.state.columns, (c) => c.key === columnKey);
        if (activeColumn && activeColumn.groupable === true && sortDirection !== 'NONE') {

            if (_.findIndex(this.state.groupBy, gr => gr === columnKey) >= 0) {
                // same column
                newState.groupBy = this.state.groupBy;
            }
            else {
                newState.groupBy = [activeColumn.key];
            }
        }
        this.setState(newState);
    }

    /**
     * Invoked after the user updates the grid rows in any way. Generates onChange.
     * @param e Information about the event
     */
    handleGridRowsUpdated(e: GridRowsUpdatedEvent): void {
        // check for each cell if the value is editable before request update of it
        const {cellKey, fromRow, toRow, updated} = e;
        const column = this.getColumn(cellKey);
        if (column) {
            let values: Array<object> = [];

            const {rowKey = 'id', onChange} = this.props;
            if (onChange) {
                for (let i = fromRow; i <= toRow; i++) {
                    const nv = updated[cellKey];
                    const oldValue = this.getRow(i);
                    if (nv !== oldValue[cellKey] && this.checkCellEditable(column, oldValue)) {
                        let value = {};
                        value[rowKey] = oldValue[rowKey];
                        value[cellKey] = nv;
                        values.push(value);
                    }
                }
                if (values.length > 0)
                    onChange(cellKey, values);
            }
        }
    }

    /** handles the requests for changing of the value state */
    handleGridCellValueStateUpdated(cellKey: string, value: object) {
        const {rowKey = 'id', onChange} = this.props;
        if (onChange) {
            let val = {};
            val[rowKey] = value[rowKey];
            val[cellKey] = value[cellKey];
            onChange(cellKey, [val]);
        }
    }

    handleFilterChange(filter: Filter) {
        let newFilters = Object.assign({}, this.state.filters);
        if (filter.filterTerm) {
            newFilters[filter.column.key] = filter;
        }
        else {
            delete newFilters[filter.column.key];
        }
        this.setState({filters: newFilters});

        if (this.props.onFilterChange)
            this.props.onFilterChange(filter);
    }

    handleClearFilters() {
        this.setState({filters: {}});
    }

    toggleFilter(overwrite?: boolean) {
        const node: any = this.node;
        if (node && node.onToggleFilter) {
            // show or hide filters
            const showFilter = overwrite !== undefined ? overwrite : this.state.showFilter === true;
            if (node.state.canFilter !== showFilter) {
                node.onToggleFilter();
            }
        }
    }

    getSelectedRows(props: Readonly<IBasicDataGridProps>, selectedRows: any[]): Array<any> {
        const curSelection = selectedRows.slice();
        let newSelection = curSelection;
        const {rowSelect = ERowSelect.Disabled} = this.props;
        const rowSelectNext = props.rowSelect === undefined ? ERowSelect.Disabled : props.rowSelect;
        if (rowSelectNext !== rowSelect) {
            switch (rowSelectNext) {
                case ERowSelect.Disabled:
                    newSelection = [];
                    break;
                case ERowSelect.Single:
                    if (newSelection.length > 1) {
                        newSelection = curSelection.slice(0, 1);
                    }
                    break;
                default: // multi
                    break;
            }
        }

        if (props.rowKey !== this.props.rowKey) {
            const oldRowKey = !this.props.rowKey ? 'id' : this.props.rowKey;
            const {rowKey = 'id'} = props;

            newSelection = this.state.sortedElements.filter(
                row => {
                    const i = newSelection.indexOf(row[oldRowKey]);
                    return i >= 0;
                }
            )
                .map(
                    (row => row[rowKey])
                );
        }
        return newSelection;
    }

    onRowsSelected(rows: Array<SelectionParams>) {
        const {rowKey = 'id'} = this.props;
        let newSelection: any[] = [];
        let addSelection: any[] = [];
        switch (this.props.rowSelect) {
            case ERowSelect.Multi:
                addSelection = rows.map(row => row.row[rowKey]);
                newSelection = this.state.selectedRows;
                break;
            case ERowSelect.Single:
                addSelection = rows.length > 0 ? [rows[0].row[rowKey]] : [];
                newSelection = [];
                break;
            default: // disabled
                break;
        }

        if (this.props.onCheckRowSelectable) {
            const curSelection = this.props.elements.filter(
                row => addSelection.indexOf(row[rowKey]) >= 0
            );
            const newSel = curSelection.filter(value => this.props.onCheckRowSelectable!(value));
            newSelection = newSelection.concat(newSel.map(row => row[rowKey]));
        }
        else {
            newSelection = newSelection.concat(addSelection);
        }
        this.setState({selectedRows: newSelection}, () => this.callRowSelectionChanged());
    }

    callRowSelectionChanged() {
        const {rowKey = 'id', elements, onRowSelectionChanged} = this.props;
        if (onRowSelectionChanged) {
            const curSelection = elements.filter(
                row => this.state.selectedRows.indexOf(row[rowKey]) >= 0
            );
            onRowSelectionChanged(curSelection);
        }
    }

    onRowsDeselected(rows: Array<SelectionParams>) {
        const {rowKey = 'id'} = this.props;
        let rowKeys = rows.map(row => row.row[rowKey]);
        let newSelection = this.state.selectedRows.filter(rk => rowKeys.indexOf(rk) === -1);
        this.setState({selectedRows: newSelection}, () => this.callRowSelectionChanged());
    }

    render() {
        if (!this.props.elements) {
            return (<div>Loading...</div>);
        }

        let props = {...this.props};
        // undocumented:
        Object.assign(props, {
            rowGroupRenderer: RowGroupRendererTranslated, // assign renderer for row groups
            columnEquality: this.isSameColumn // assigne columnEquality to inform the table reload columns
        });

        const {
            rowKey = 'id',
            rowSelect = ERowSelect.Disabled,
            headerRowHeight = 35,
            minColumnWidth = 20,
            showSelectAll = true,
        } = this.props;

        this.toggleFilter();

        const rdg = (
            <ReactDataGrid
                ref={ref => this.node = ref}
                {...props}
                minColumnWidth={minColumnWidth}
                headerRowHeight={headerRowHeight}
                rowHeight={this.state.rowHeight}
                enableCellSelect={true}
                columns={this.state.columns}
                rowGetter={this.getRow}
                rowsCount={this.getRows().length}
                onGridSort={this.handleGridSort}
                onGridRowsUpdated={this.handleGridRowsUpdated}
                // TODO: add property editOnSelect = true/false and by true add the code below, separate per row!
                // onCellSelected={(coordinates: {rowIdx: number, idx: number}) =>
                // {
                //   if (this.node)
                //   {
                //     // for (let i: number = 0; i < 3; i++)
                //     //   this.node.openCellEditor(coordinates.rowIdx, i);
                //     this.node.openCellEditor(coordinates.rowIdx, coordinates.idx);
                //   }
                // }}
                // onRowClick={(e: any) => console.log("onRowClick", e)}
                // onCellSelected={(e: any) => console.log("onCellSelected", e)}
                // onCellDeSelected={(e: any) => console.log("onCellDeSelected", e)}
                // onCheckCellIsEditable={}
                // onCellDoubleClick={}
                // rowRenderer={RowRenderer}

                // enableRowSelect={!this.props.rowSelect || this.props.rowSelect === ERowSelect.Disabled ? false : this.props.rowSelect}

                rowSelection={
                    {
                        showCheckbox: rowSelect !== ERowSelect.Disabled,
                        enableShiftSelect: false,
                        onRowsSelected: this.onRowsSelected,
                        onRowsDeselected: this.onRowsDeselected,
                        selectBy:
                            {
                                keys:
                                    {
                                        rowKey: rowKey,
                                        values: this.state.selectedRows
                                    }
                            }
                    }
                }

                // hide checkbox if showSelectAll is false
                selectAllRenderer={showSelectAll ? undefined : () => <div />}

                // FIXME: bug in the ReactDataGrid - Uncaught TypeError: Cannot read property 'canvas' of undefined
                // emptyRowsView={() => <EmptyRowsViewTrans />}

                onAddFilter={(filter: any) => this.handleFilterChange(filter)}
                onClearFilters={this.handleClearFilters}

                onColumnResize={this.handleColumnResize}

                enableDragAndDrop={true}
                onRowExpandToggle={this.onRowExpandToggle}
            />);

        if (this.props.enableReorderColumns === true) {
            return (
                <DraggableHeader.DraggableContainer
                    onHeaderDrop={this.onHeaderDrop}
                >
                    {rdg}
                </DraggableHeader.DraggableContainer>
            );
        }
        else {
            return rdg;
        }
    }

    private isSameColumn(a: IColumnDefinition, b: IColumnDefinition) {
        // undocumented: will be called when the columns should be reordered
        return this.state.isReordering ? false : deepEqual(a, b, {strict: false});
    }

    private onHeaderDrop(sourceColKey: string, targetColKey: string) {
        const columnsCopy = this.state.columns.map(column => {return {...column};});
        const columnSourceIndex = _.findIndex(
            columnsCopy,
            i => i.key === sourceColKey
        );
        const columnTargetIndex = _.findIndex(
            columnsCopy,
            i => i.key === targetColKey
        );

        columnsCopy.splice(
            columnTargetIndex,
            0,
            columnsCopy.splice(columnSourceIndex, 1)[0]
        );

        const {showFilter} = this.state;

        this.setState(
            {columns: columnsCopy, isReordering: true, showFilter: false},
            () => {
                if (this.props.onReorderColumns) {
                    this.props.onReorderColumns(columnsCopy.map(column => column.key));
                }
                this.setState({isReordering: false, showFilter});
            }
        );
    }

    private onRowExpandToggle(props: OnRowExpandToggle) {
        const {columnGroupName, name, shouldExpand} = props;
        let expandedRows = Object.assign({}, this.state.expandedRows);
        expandedRows[columnGroupName] = Object.assign({}, expandedRows[columnGroupName]);
        expandedRows[columnGroupName][name] = {isExpanded: shouldExpand};
        this.setState({expandedRows: expandedRows});
    }

    private handleColumnResize(index: number, width: number) {
        if (index > 0 &&
            (this.props.rowSelect === ERowSelect.Single ||
                this.props.rowSelect === ERowSelect.Multi)) {
            // the index includes the row selection column if row selection is enabled
            index = index - 1;
        }

        // just remember new width to avoid artifacts when the column will be updated, see DerivateOptionColumn
        this.state.columns[index].width = width;
    }

    private checkCellEditable(column: IColumnDefinition, rowData: any): boolean {
        if (this.props.editable !== true) {
            return false;
        }

        let editMode: EditableValue | undefined = undefined;
        if (column.keyEditable) {
            editMode = BaseColumn.getEditModeFromKeyEditable(column.keyEditable, rowData);
        }
        return editMode === EditableValue.Editable;
    }

    private onChangeDerivateVisible(originIndex: number, derivate: string, visible: boolean): void {
        if (originIndex < this.props.columns.length) {
            const columns = this.state.columns.slice();
            let columnToUpdateIndex = _.findIndex(columns, (value: IColumnDefinition) => value.originIndex === originIndex);

            if (columnToUpdateIndex >= 0) {
                // make copy of column!!!
                let columnToUpdate: IDerivateOptionColumnDefinition = {...columns[columnToUpdateIndex] as IDerivateOptionColumnDefinition};

                // make copy of hideDerivates!!!
                let hideDerivates: string[] | undefined = columnToUpdate.hideDerivates ? [...columnToUpdate.hideDerivates] : [];
                if (visible) // remove from list of hideDerivates
                {
                    hideDerivates = hideDerivates.filter(value => value !== derivate);
                }
                else if (_.findIndex(hideDerivates, value => value === derivate) < 0) // add to list of hideDerivates
                {
                    hideDerivates.push(derivate);
                }
                if (hideDerivates.length === 0) {
                    hideDerivates = undefined;
                }
                columnToUpdate.hideDerivates = hideDerivates;

                columns[columnToUpdateIndex] = columnToUpdate;
            }
            this.setState({columns}, () => {
                const originColumn = this.props.columns[originIndex] as DerivateOptionColumnDefinition;
                if (originColumn.onChangeDerivateVisible) {
                    originColumn.onChangeDerivateVisible(derivate, visible);
                }
            });
        }
    }

    private calcFactorShownDerivates(column: IDerivateOptionColumnDefinition): number {
        if (!column.hideDerivates || column.editDerivates) {
            return 1;
        }
        else {
            const fullLen = column.derivates.size;
            const shownLen = Array.from(column.derivates.values()).filter(derivate => column.hideDerivates ?
                _.findIndex(column.hideDerivates, value => value === derivate) < 0
                : true).length;
            return shownLen / fullLen;
        }
    }

    /** calculates the row height based on height in all columns */
    private static calcRowHeight(props: IBasicDataGridProps): number {
        const {columns, valueStateHeight = 0} = props;
        let rowHeight = 5;
        columns.forEach(value => {
            const {minRowHeight = 35} = value;
            if (minRowHeight > rowHeight)
                rowHeight = minRowHeight;
        });
        return rowHeight + valueStateHeight;
    }

    /** maps the properties to ReactDataGrid.Column */
    private mapColumns(props: IBasicDataGridProps, prev?: {props: IBasicDataGridProps, columns: Array<IColumnDefinition>}): Array<IColumnDefinition> {
        const columns: Array<Column> = props.columns;
        const internalColumns: Array<IColumnDefinition> = [];
        columns.forEach((element, index) => {
            const isDerivateColumn = ColumnFactory.isDerivateColumn(element);
            const internalEl = isDerivateColumn ? {...element} as IDerivateOptionColumnDefinition : {...element} as IColumnDefinition;

            internalEl.draggable = props.enableReorderColumns;
            internalEl.originIndex = index;
            const {rowKey = 'id'} = props;
            internalEl.rowKey = rowKey;
            const CellRenderer = element.cellRenderer ? element.cellRenderer : BaseColumn;
            internalEl.formatter = CellRenderer;
            internalEl.editor = CellRenderer;
            internalEl.valueStateHeight = props.valueStateHeight;

            // each column has a reference to its data grid to control focus when needed
            internalEl.getGrid = () => this.node;

            // in not edit mode the informations about rowdata and column are not available,
            // so put it by rowMetaData (see dependentValues)
            internalEl.getRowMetaData = (rowdata: any, column: Column) => {return {rowdata, column};};

            if (internalEl.valueState) {
                internalEl.valueState.onChange = this.handleGridCellValueStateUpdated.bind(this);
            }

            if (internalEl.filterable) {
                // assign filter renderer and check method from the BaseColumn
                internalEl.filterRenderer = CellRenderer.filterRenderer;
                internalEl.filterValid = CellRenderer.filterValid;
            }

            // undocumented!!! set editable as function to check, when the cell is selected for editing
            // Object.assign(internalEl, { editable: true }); // allows copy of not editable fields
            // not allows copy of not editable fields, but has no artefact of input value in not edit field
            Object.assign(internalEl, {editable: (rowData: any) => this.checkCellEditable(internalEl, rowData)});

            if (isDerivateColumn) {
                internalEl.onChangeDerivateVisible = (derivate: string, visible: boolean) =>
                    this.onChangeDerivateVisible(index, derivate, visible);
            }

            if (prev && prev.props.columns.length === prev.columns.length && prev.props.columns.length > index) {
                const prevColumn = prev.props.columns[index];
                const prevInternalColumn = prev.columns[index];
                // copy width to prevent changing of widths
                if (prevColumn.key === element.key && internalEl.key === prevInternalColumn.key && prevColumn.width === element.width && prevInternalColumn.width) {
                    const f = isDerivateColumn ? this.calcFactorShownDerivates(prevInternalColumn as IDerivateOptionColumnDefinition) : 1;
                    internalEl.width = prevInternalColumn.width / f;
                }

                if (isDerivateColumn && element.hideDerivates === prevColumn.hideDerivates) {
                    // check the hiddenDerivates was changed, otherwise copy internal
                    internalEl.hideDerivates = prevInternalColumn.hideDerivates;
                }
            }

            // recalc depending on column.editDerivates
            if (isDerivateColumn && internalEl.width !== undefined) {
                const f = this.calcFactorShownDerivates(internalEl as IDerivateOptionColumnDefinition);
                internalEl.width = internalEl.width * f;
            }

            // groupable needs sortable to be set
            if (internalEl.groupable === true) {
                internalEl.sortable = true;
            }

            internalColumns.push(internalEl);
        });
        return internalColumns;
    }
}

/**
 * Check against navigation keys that may cause the data grid to switch cells.
 * Necessary in case of multi line text fields where the user should be able to
 * navigate inside the cell and add new lines.
 */
export const isNavigationKey = (key: string): boolean => {
    // Keyboard keys that may cause the data grid to switch cells.
    switch (key) {
        case "Enter":
        case "Backspace":
        case "ArrowUp":
        case "ArrowDown":
        case "ArrowLeft":
        case "ArrowRight":
            return true;
        default:
            return false;
    }
};

export default BasicDataGrid;



// WEBPACK FOOTER //
// ./src/components/BasicDataGrid.tsx